import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";

import { createLegalService } from "@/core/services/factory";
import { useTranslations } from "@/lib/useTranslations";

export function LegalDonateLinkCard() {
  const t = useTranslations("legal");
  const legalService = useMemo(() => createLegalService(), []);

  const [value, setValue] = useState("");
  const [savedValue, setSavedValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const link = await legalService.getDonateLink();
    setValue(link);
    setSavedValue(link);
    setLoading(false);
  }, [legalService]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = value.trim() !== savedValue.trim();

  const onSave = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const saved = await legalService.updateDonateLink(value);
      if (saved == null) {
        setError(t("donateLinkError"));
        return;
      }
      setValue(saved);
      setSavedValue(saved);
      setInfo(t("donateLinkSuccess"));
    } catch {
      setError(t("donateLinkError"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-black/10 bg-white px-4 py-6 text-sm text-neutral-500 shadow-sm">
        {t("loading")}
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => void onSave(e)}
      className="space-y-3 rounded-xl border border-black/10 bg-white p-4 shadow-sm"
    >
      <div className="space-y-1">
        <h2 className="text-sm font-semibold text-whart-text">
          {t("donateLinkTitle")}
        </h2>
        <p className="text-sm text-neutral-600">{t("donateLinkHelp")}</p>
      </div>

      {error && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}
      {info && !error && (
        <p className="rounded-xl border border-black/10 bg-zinc-50 px-3 py-2 text-sm text-zinc-800">
          {info}
        </p>
      )}

      <label className="block space-y-1.5">
        <span className="text-xs uppercase tracking-wider text-neutral-500">
          {t("donateLinkField")}
        </span>
        <input
          type="url"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setInfo(null);
          }}
          placeholder="https://"
          className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-whart-text outline-none focus:border-black/20"
        />
      </label>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving || !dirty}
          className="rounded-xl bg-yellow-100 px-4 py-2 text-sm font-medium text-zinc-900 transition hover:bg-amber-100 disabled:opacity-50"
        >
          {saving ? t("saving") : t("donateLinkSave")}
        </button>
      </div>
    </form>
  );
}
